import { helpers } from "../util/helpers.ts";
import { formatSeasonRecord } from "../util/seasonRecord.ts";
import { translateRoundsWonText } from "../util/roundsWonText.ts";
import { i18n } from "../util/useT.ts";

// M3-5: moved out of the deleted views/Message/ page into a shared component so
// the InboxSheet letter level can render the owner-trust history chart. The prop
// type is now the getMessageSheet RPC's augmented owner-mood row (View<"message">
// died with the page); the rendering is otherwise unchanged.
export type MessageSheetOwnerMood = {
	money: number;
	playoffs: number;
	wins: number;
	total: number;
	season: number;
	seasonInfo:
		| {
				won: number;
				lost: number;
				tied: number;
				otl: number;
				champ: boolean;
				profit: number;
				roundsWonText: string;
		  }
		| undefined;
};

const formatMood = (value: number) => value.toFixed(1);

const formatDelta = (value: number) => {
	const formatted = formatMood(value);
	return value > 0 ? `+${formatted}` : formatted;
};

const OwnerMoodsChart = ({
	ownerMoods,
}: {
	ownerMoods: MessageSheetOwnerMood[];
}) => {
	const latest = ownerMoods.at(-1);
	if (!latest) {
		return null;
	}

	const previous = ownerMoods.at(-2);
	const scoreRows: {
		key: keyof Pick<MessageSheetOwnerMood, "wins" | "playoffs" | "money" | "total">;
		label: string;
		className: string;
	}[] = [
		{
			key: "total",
			label: i18n.t("ownerMoodsChart.total", {
				ns: "ui",
				defaultValue: "Total",
			}),
			className: "fw-bold text-dark",
		},
		{
			key: "wins",
			label: i18n.t("ownerMoodsChart.regularSeason", {
				ns: "ui",
				defaultValue: "Regular season success",
			}),
			className: "text-danger",
		},
		{
			key: "playoffs",
			label: i18n.t("ownerMoodsChart.playoffs", {
				ns: "ui",
				defaultValue: "Playoff success",
			}),
			className: "text-info",
		},
		{
			key: "money",
			label: i18n.t("ownerMoodsChart.finances", {
				ns: "ui",
				defaultValue: "Finances",
			}),
			className: "text-success",
		},
	];
	const recent = [...ownerMoods].slice(-3).reverse();

	return (
		<div className="mt-3">
			<h2 className="h5 mb-2">
				{i18n.t("ownerMoodsChart.title", {
					ns: "ui",
					defaultValue: "Owner Mood History",
				})}
			</h2>

			<div className="row g-2">
				{scoreRows.map(({ key, label, className }) => {
					const delta =
						previous !== undefined ? latest[key] - previous[key] : undefined;

					return (
						<div className="col-12 col-sm-3" key={key}>
							<div className="border rounded p-2 h-100">
								<div className="small text-body-secondary">{label}</div>
								<div className={className}>{formatMood(latest[key])}</div>
								{delta !== undefined ? (
									<div className="small text-body-secondary">
										{formatDelta(delta)}
									</div>
								) : null}
							</div>
						</div>
					);
				})}
			</div>

			{latest.seasonInfo ? (
				<p className="small text-body-secondary mt-2 mb-0">
					{latest.season}: {formatSeasonRecord(latest.seasonInfo)},{" "}
					{latest.seasonInfo.roundsWonText ? (
						<>
							{translateRoundsWonText(latest.seasonInfo.roundsWonText)},{" "}
						</>
					) : null}
					{i18n.t("ownerMoodsChart.profit", {
						ns: "ui",
						defaultValue: "Profit:",
					})}{" "}
					{helpers.formatCurrency(latest.seasonInfo.profit, "M")}
				</p>
			) : null}

			<ul className="list-unstyled small text-body-secondary mt-2 mb-0">
				{recent.map((mood) => (
					<li key={mood.season}>
						<span className="fw-semibold">{mood.season}</span>:{" "}
						{i18n.t("ownerMoodsChart.total", {
							ns: "ui",
							defaultValue: "Total",
						})}{" "}
						{formatMood(mood.total)}
					</li>
				))}
			</ul>

		</div>
	);
};

export default OwnerMoodsChart;
